'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { getCurrentSession } from '@/lib/quiz-session';
import GroupCube, { type GroupCubePoint } from '@/components/GroupCube';
import PageShell from '@/components/PageShell';
import Button from '@/components/ui/Button';
import { ArrowLeft, ArrowRight } from 'lucide-react';

// Past-self comparison. Reuses the GroupCube infrastructure to plot past
// quiz results (this device only) against the current one. Sourced from
// localStorage rather than Firestore because Firestore rules currently block
// client-side reads of quizResponses (those go through the Admin SDK route).
//
// Limitation: long-quiz Phase 2 doesn't surface in localStorage cube
// positions, so this page compares Phase 1 positions which are close to but
// not identical to the long-quiz final result.

interface HistoryEntry {
  sessionId: string;
  type: 'short' | 'long';
  completedAt: number;
  position: { x: number; y: number; z: number };
}

const STORAGE_KEY = 'votely_quiz_session_all';

function loadHistory(): HistoryEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const all = JSON.parse(raw) as Record<string, {
      sessionId: string;
      type: 'short' | 'long';
      completedAt?: number;
      phase1Scores?: { economic: number; governance: number; social: number };
    }>;
    const out: HistoryEntry[] = [];
    for (const v of Object.values(all)) {
      if (!v.completedAt || !v.phase1Scores) continue;
      out.push({
        sessionId: v.sessionId,
        type: v.type,
        completedAt: v.completedAt,
        position: {
          x: v.phase1Scores.economic,
          y: v.phase1Scores.governance,
          z: v.phase1Scores.social,
        },
      });
    }
    return out.sort((a, b) => b.completedAt - a.completedAt);
  } catch {
    return [];
  }
}

const COLORS = ['#8e6cf0', '#22c55e', '#ef4444', '#f59e0b', '#06b6d4', '#ec4899'];

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function HistoryPage() {
  const [history, setHistory] = useState<HistoryEntry[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const h = loadHistory();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time hydrate from localStorage on mount, mirrors LocaleProvider pattern
    setHistory(h);
    // Pre-select the two most recent entries.
    const initialSelection = new Set(h.slice(0, 2).map((e) => e.sessionId));
    setSelected(initialSelection);
    // Touch session loader so any sessionStorage-only entry surfaces too if
    // the user just landed here from a fresh quiz. (No-op if nothing.)
    void getCurrentSession();
  }, []);

  const points: GroupCubePoint[] = useMemo(() => {
    if (!history) return [];
    // history is most-recent first. For the trail to read chronologically
    // (oldest -> newest), reverse before mapping. The most-recent entry
    // becomes the last point and gets the "you" highlight.
    const chosen = history.filter((e) => selected.has(e.sessionId)).slice().reverse();
    return chosen.map((e, i) => {
      const isMostRecent = i === chosen.length - 1;
      return {
        userId: e.sessionId,
        label: isMostRecent ? 'most recent' : fmtDate(e.completedAt),
        x: e.position.x,
        y: e.position.y,
        z: e.position.z,
        color: COLORS[i % COLORS.length],
        isYou: isMostRecent,
      };
    });
  }, [history, selected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (history === null) {
    return (
      <PageShell maxWidth="md">
        <div className="flex items-center justify-center py-24">
          <div className="text-center space-y-4" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto" aria-hidden="true"></div>
            <p className="text-xl text-foreground">Loading your history…</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (history.length === 0) {
    return (
      <PageShell maxWidth="md">
        <div className="bg-white dark:bg-foreground/[0.07] rounded-2xl shadow-lg p-8 mt-12 text-center">
          <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-900 px-4 py-2 rounded-full text-sm font-medium mb-4 uppercase tracking-wider">
            Your timeline
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">No past results yet</h1>
          <p className="text-foreground/70 mb-6">
            Take the quiz once and your results will be saved on this device so
            you can compare yourself over time.
          </p>
          <Button
            href="/quiz?type=short"
          >
            Take the quiz
          </Button>
        </div>
      </PageShell>
    );
  }

  if (history.length === 1) {
    return (
      <PageShell maxWidth="md">
        <div className="bg-white dark:bg-foreground/[0.07] rounded-2xl shadow-lg p-8 mt-12 text-center">
          <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-900 px-4 py-2 rounded-full text-sm font-medium mb-4 uppercase tracking-wider">
            Your timeline
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Only one result so far</h1>
          <p className="text-foreground/70 mb-4">
            Take the quiz again to see how you&apos;ve moved over time. Your
            previous result is saved on this device.
          </p>
          <p className="text-sm text-foreground/60 mb-6">
            Most recent: {fmtDate(history[0].completedAt)}
          </p>
          <Button
            href="/quiz?type=short"
          >
            Retake the quiz
          </Button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="3xl">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-[#2a4173] text-white px-4 py-2 rounded-full text-sm font-medium mb-4 uppercase tracking-wider">
          Your timeline
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-foreground">
          Your results over time
        </h1>
        <p className="text-foreground/60 mt-3">
          This device has <strong>{history.length}</strong> past results. Pick the ones you want to compare.
        </p>
      </div>

      <div className="bg-white dark:bg-foreground/[0.07] rounded-2xl shadow-lg p-8">
        <div className="rounded-xl overflow-hidden bg-foreground/5 mb-6">
          {points.length >= 1 ? (
            <GroupCube
              points={points}
              showCentroid={false}
              connectPoints={points.length >= 2}
            />
          ) : (
            <div className="aspect-square flex items-center justify-center text-foreground/70">
              Select at least one result below.
            </div>
          )}
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-4" id="past-results-heading">
          Past results
        </h2>
        <ul className="space-y-2" aria-labelledby="past-results-heading">
          {history.map((e, i) => {
            const checkboxId = `history-row-${e.sessionId}`;
            const checked = selected.has(e.sessionId);
            return (
              <li key={e.sessionId}>
                {/* Whole row is a clickable label tied to the checkbox.
                    Keyboard users tab to the checkbox and press Space; click
                    anywhere on the row also toggles. */}
                <label
                  htmlFor={checkboxId}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer transition focus-within:ring-2 focus-within:ring-slate-500 focus-within:ring-offset-2 ${
                    checked
                      ? 'bg-slate-50 border-2 border-slate-300'
                      : 'bg-foreground/5 border-2 border-transparent hover:border-foreground/20'
                  }`}
                >
                  <div>
                    <div className="font-semibold text-foreground">
                      {fmtDate(e.completedAt)}
                      {i === 0 && <span className="ml-2 text-xs font-bold text-slate-800 uppercase tracking-wide">most recent</span>}
                    </div>
                    <div className="text-xs text-foreground/70">
                      {e.type} quiz, econ {Math.round(e.position.x)}, gov {Math.round(e.position.y)}, social {Math.round(e.position.z)}
                    </div>
                  </div>
                  <input
                    id={checkboxId}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(e.sessionId)}
                    className="w-5 h-5 accent-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
                    aria-label={`Include the ${fmtDate(e.completedAt)} ${e.type} quiz result in the comparison`}
                  />
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex justify-between items-center text-sm mt-6">
        <Link
          href="/"
          className="inline-flex items-center gap-1.5 text-foreground/70 hover:text-foreground rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Home
        </Link>
        <Link
          href="/quiz?type=short"
          className="inline-flex items-center gap-1.5 text-slate-800 hover:text-slate-900 font-medium rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
        >
          Take it again <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </div>
    </PageShell>
  );
}
